<template>
  <div class="analysis-row" @click="navigateToDetails">
    <div class="analysis-info">
      <h3 class="company-name">{{ analysis.company_name }}</h3>
    </div>
    
    <!-- Show scores only if status is completed -->
    <div v-if="analysis.status === 'completed'" class="analysis-scores">
      <div class="score-item">
        <span class="score-label">Community</span>
        <span class="score-value">{{ formatScore(communityScore) }}</span>
      </div>
      <div class="score-item">
        <span class="score-label">Storytelling</span>
        <span class="score-value">{{ formatScore(storytellingScore) }}</span>
      </div>
      <div class="score-item">
        <span class="score-label">Experience</span>
        <span class="score-value">{{ formatScore(experienceScore) }}</span>
      </div>
    </div>
    
    <div class="analysis-status">
      <StatusBadge :status="analysis.status" />
    </div>
    <div class="analysis-date">
      <span class="date-text">{{ formattedDate }}</span>
    </div>
  </div>
</template>

<script>
import StatusBadge from './StatusBadge.vue';

export default {
  name: 'AnalysisCard',
  components: {
    StatusBadge,
  },
  props: {
    analysis: {
      type: Object,
      required: true,
    },
  },
  computed: {
    formattedDate() {
      if (!this.analysis.created_at) return '';

      const date = new Date(this.analysis.created_at);
      const options = {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      };

      return date.toLocaleDateString('it-IT', options);
    },
    communityScore() {
      return this.analysis?.community_score?.community_score || 0;
    },
    storytellingScore() {
      return this.analysis?.storytelling_score?.storytelling_score || 0;
    },
    experienceScore() {
      return this.analysis?.experience_score?.experience_score || 0;
    },
  },
  methods: {
    navigateToDetails() {
      // Only navigate if analysis is completed
      if (this.analysis.status === 'completed') {
        this.$router.push(`/analisi/${this.analysis.analysis_id}`);
      }
    },
    formatScore(score) {
      // Scores are from 0-10, multiply by 10 to get percentage
      return `${Math.round(score * 10)}/100`;
    },
  },
};
</script>

<style scoped>
.analysis-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 80px;
  padding: 1rem 1.5rem;
  border: 2px solid #2e4eed;
  background: white;
  transition: all 0.3s ease;
  cursor: pointer;
  gap: 1rem;
}

.analysis-row:hover {
  background: rgba(86, 188, 238, 0.05);
  box-shadow: 0 4px 12px rgba(46, 78, 237, 0.1);
}

.analysis-info {
  flex: 1;
  min-width: 0; /* Allow text truncation */
}

.company-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.analysis-scores {
  display: flex;
  gap: 1.5rem;
  flex-shrink: 0;
}

.score-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.score-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.score-value {
  font-size: 1rem;
  font-weight: 600;
  color: #2e4eed;
}

.analysis-status {
  flex-shrink: 0;
}

.analysis-date {
  flex-shrink: 0;
  text-align: right;
  min-width: 180px;
}

.date-text {
  font-size: 0.875rem;
  color: #6b7280;
}

/* Mobile responsive */
@media (max-width: 1024px) {
  .analysis-scores {
    gap: 1rem;
  }

  .score-label {
    font-size: 0.7rem;
  }

  .score-value {
    font-size: 0.9rem;
  }
}

@media (max-width: 768px) {
  .analysis-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem;
    min-height: auto;
  }

  .analysis-info {
    width: 100%;
  }

  .company-name {
    font-size: 1.125rem;
  }

  .analysis-scores {
    width: 100%;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .analysis-status {
    align-self: flex-start;
  }

  .analysis-date {
    align-self: flex-start;
    min-width: auto;
    text-align: left;
  }

  .date-text {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .company-name {
    font-size: 1rem;
  }

  .analysis-scores {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }

  .score-item {
    flex-direction: row;
    gap: 0.5rem;
  }

  .score-label {
    font-size: 0.75rem;
  }

  .score-value {
    font-size: 0.875rem;
  }
}
</style>
